// NotificationsV2 Integration Service - Connects Phase 2 with existing application

import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'NotificationsV2Integration', level: 'info' });

// Event types that trigger notifications
export interface UserRegisteredEvent {
  email: string;
  registration_method: string;
  source: string;
}

export interface NoteCreatedEvent {
  note_id: string;
  title: string;
  content: string;
  tags?: string[];
}

export interface CardCreatedEvent {
  card_id: string;
  title: string;
  type: string;
  tags?: string[];
}

export interface ReviewCompletedEvent {
  review_id: string;
  review_name: string;
  word_count: number;
  cards_count: number;
  notes_count: number;
}

export interface AchievementUnlockedEvent {
  achievement_type: string;
  achievement_name: string;
  description: string;
  xp_earned: number;
  badge_icon?: string;
}

export interface LevelUpEvent {
  previous_level: string;
  new_level: string;
  xp_earned: number;
  total_xp: number;
  benefits?: string[];
}

export interface StreakMilestoneEvent {
  streak_type: string;
  streak_days: number;
  reward?: string;
  next_milestone?: string;
}

// Main integration service
class NotificationsV2Integration {
  private static instance: NotificationsV2Integration;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): NotificationsV2Integration {
    if (!NotificationsV2Integration.instance) {
      NotificationsV2Integration.instance = new NotificationsV2Integration();
    }
    return NotificationsV2Integration.instance;
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      logger.debug('NotificationsV2 already initialized');
      return;
    }

    try {
      logger.info('Initializing NotificationsV2 system...');
      
      // Check if notification templates exist
      const { data: templates, error } = await supabase
        .from('notification_templates')
        .select('key')
        .eq('active', true);

      if (error) {
        throw new Error(`Failed to check notification templates: ${error.message}`);
      }

      if (!templates || templates.length === 0) {
        logger.warn('No notification templates found. Please run the template creation script.');
      } else {
        logger.info(`Found ${templates.length} active notification templates`);
      }

      // Check if events table exists
      const { error: eventsError } = await supabase
        .from('events')
        .select('id')
        .limit(1);

      if (eventsError) {
        throw new Error(`Events table not accessible: ${eventsError.message}`);
      }

      this.isInitialized = true;
      logger.info('NotificationsV2 system initialized successfully');
    } catch (error) {
      logger.error('Failed to initialize NotificationsV2 system:', error);
      throw error;
    }
  }

  private async publishEvent(eventType: string, userId: string, data: Record<string, any>): Promise<void> {
    try {
      logger.debug(`Publishing event: ${eventType} for user: ${userId}`, data);

      // Store the event in the events table for processing
      const { error } = await supabase
        .from('events')
        .insert({
          event_type: eventType,
          event_version: 1,
          aggregate_id: userId, // User is the aggregate for most events
          aggregate_type: 'user',
          user_id: userId,
          event_data: data,
          metadata: {
            source: 'web_app',
            timestamp: new Date().toISOString()
          },
          created_at: new Date().toISOString()
        });

      if (error) {
        throw new Error(`Failed to store event: ${error.message}`);
      }

      logger.debug(`Event stored successfully: ${eventType} for user: ${userId}`);
    } catch (error) {
      logger.error(`Error publishing event ${eventType}:`, error);
      // Don't throw - we don't want notification failures to break user actions
    }
  }

  // Event publishing methods
  public async userRegistered(userId: string, data: UserRegisteredEvent): Promise<void> {
    await this.publishEvent('user.registered', userId, data);
  }

  public async noteCreated(userId: string, data: NoteCreatedEvent): Promise<void> {
    await this.publishEvent('note.created', userId, data);
  }

  public async cardCreated(userId: string, data: CardCreatedEvent): Promise<void> {
    await this.publishEvent('card.created', userId, data);
  }

  public async reviewCompleted(userId: string, data: ReviewCompletedEvent): Promise<void> {
    await this.publishEvent('review.completed', userId, data);
  }

  public async achievementUnlocked(userId: string, data: AchievementUnlockedEvent): Promise<void> {
    await this.publishEvent('achievement.unlocked', userId, data);
  }

  public async levelUp(userId: string, data: LevelUpEvent): Promise<void> {
    await this.publishEvent('level.up', userId, data);
  }

  public async streakMilestone(userId: string, data: StreakMilestoneEvent): Promise<void> {
    await this.publishEvent('streak.milestone', userId, data);
  }
}

// Export singleton instance and helper functions
export const notificationsV2 = NotificationsV2Integration.getInstance();

export const initializeNotificationsV2 = async (): Promise<void> => {
  await notificationsV2.initialize();
};

// Convenient event publishing functions
export const NotificationEvents = {
  userRegistered: (userId: string, data: UserRegisteredEvent) => 
    notificationsV2.userRegistered(userId, data),
  
  noteCreated: (userId: string, data: NoteCreatedEvent) => 
    notificationsV2.noteCreated(userId, data),
  
  cardCreated: (userId: string, data: CardCreatedEvent) => 
    notificationsV2.cardCreated(userId, data),
  
  reviewCompleted: (userId: string, data: ReviewCompletedEvent) => 
    notificationsV2.reviewCompleted(userId, data),
  
  achievementUnlocked: (userId: string, data: AchievementUnlockedEvent) => 
    notificationsV2.achievementUnlocked(userId, data),
  
  levelUp: (userId: string, data: LevelUpEvent) => 
    notificationsV2.levelUp(userId, data),
  
  streakMilestone: (userId: string, data: StreakMilestoneEvent) => 
    notificationsV2.streakMilestone(userId, data),
}; 